import { ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import Skeleton from "@galaxy-io/dls/feedback/Skeleton";
import Icon, { IconVariant } from "@galaxy-io/dls/icons/Icon";
import Box from "@galaxy-io/dls/layout/Box";
import Flex, { AlignItems, FlexDirection } from "@galaxy-io/dls/layout/Flex";
import Code from "@galaxy-io/dls/text/Code";
import Text, { TextSize, TextVariant, TextWeight } from "@galaxy-io/dls/text/Text";
import { t } from "@galaxy-io/dls/theme/tokens/t";
import Widget, { WidgetVariant } from "@galaxy-io/dls/widget/Widget";
import {
  type ExclusionInput,
  exclusionFields,
  type exclusionKinds,
} from "@gtm/contracts/core/exclusions";
import { styled } from "@linaria/react";
import {
  AtIcon,
  BuildingOfficeIcon,
  CaretRightIcon,
  FunnelSimpleIcon,
  ProhibitIcon,
  UserIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { type ComponentProps, type ReactNode, useState } from "react";
import { Link, useRouter } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { useRetainedValue } from "@/components/workspace/use-retained-value";
import { SendMenu } from "@/features/activity/send-menu";
import { apiClient, unwrap } from "@/lib/api-client";
import { emptyRule, RuleForm, useExclusionActions } from "./rule-form";

/**
 * The dialog reads as labelled blocks of choices: a quiet caption over a
 * hairline card whose rows are left-aligned icon · copy · chevron.
 */
const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${t.space[8]};
  min-width: 0;
`;
const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${t.space[4]};
  min-width: 0;
`;
const Copy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${t.space[2]};
  min-width: 0;
`;
const Note = styled.div`
  padding: ${t.space[12]};
  min-width: 0;
`;
const targetIcons = {
  person: UserIcon,
  company: BuildingOfficeIcon,
  identity: AtIcon,
} as const;
// The word the accessible name uses, so a target reads as the thing it hides.
const targetWords = {
  person: "person",
  company: "company",
  identity: "account",
} as const;
const targetHints = {
  person: "Excludes this person and their signals.",
  company: "Excludes this company, its people and their signals.",
  identity: "Excludes this provider account and its signals.",
} as const;
const providerNames: Record<string, string> = {
  github: "GitHub",
  reddit: "Reddit",
};
function Choice({
  icon,
  title,
  description,
  ariaLabel,
  disabled,
  onClick,
}: {
  icon: ComponentProps<typeof Icon>["component"];
  title: ReactNode;
  description?: string;
  ariaLabel: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Widget isInteractive ariaLabel={ariaLabel} isDisabled={disabled} onClick={onClick}>
      <Flex gap={12} alignItems={AlignItems.CENTER}>
        <Icon component={icon} size={16} variant={IconVariant.SECONDARY} />
        <Copy>
          <Text size={TextSize.BODY_MD} weight={TextWeight.MEDIUM} lineClamp={1}>
            {title}
          </Text>
          {description && (
            <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
              {description}
            </Text>
          )}
        </Copy>
        <Icon component={CaretRightIcon} size={12} variant={IconVariant.TERTIARY} />
      </Flex>
    </Widget>
  );
}
function Block({
  label,
  caption,
  children,
}: {
  label: string;
  caption?: string;
  children: ReactNode;
}) {
  return (
    <Section>
      <Heading>
        <Text size={TextSize.BODY_SM} weight={TextWeight.MEDIUM}>
          {label}
        </Text>
        {caption && (
          <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
            {caption}
          </Text>
        )}
      </Heading>
      <Flex direction={FlexDirection.COLUMN} gap={8}>
        {children}
      </Flex>
    </Section>
  );
}
type Kind = (typeof exclusionKinds)[number];
export function useExclusionContext(kind: Kind, id: string, enabled = !!id) {
  return useQuery({
    queryKey: ["exclusion-context", kind, id],
    enabled,
    queryFn: () =>
      unwrap(
        apiClient().GET("/exclusions/context/{kind}/{id}", {
          params: { path: { kind, id } },
        }),
      ),
  });
}
export function ExclusionActions({
  kind,
  id,
  sendKey,
  trigger,
}: {
  kind: Kind;
  id: string;
  sendKey?: string;
  /** Replaces the default button, e.g. with a menu item. */
  trigger?: (open: () => void, excluded: boolean) => ReactNode;
}) {
  const query = useExclusionContext(kind, id),
    router = useRouter(),
    actions = useExclusionActions();
  const [open, setOpen] = useState(false),
    [initial, setInitial] = useState<ExclusionInput | null>(null);
  const retainedInitial = useRetainedValue(initial);
  const excluded = !!query.data?.reasons.length;
  return (
    <>
      {trigger ? (
        trigger(() => setOpen(true), excluded)
      ) : (
        <Button
          label={excluded ? "Excluded" : "Exclude…"}
          icon={ProhibitIcon}
          variant={ButtonVariant.SECONDARY}
          onClick={() => setOpen(true)}
        />
      )}
      {kind === "person" && sendKey && (
        <SendMenu keys={[sendKey]} disabled={excluded || !query.data} />
      )}
      <SurfaceDialog
        open={open}
        title="Exclude records"
        headerDescription="Exclude this record, or create a rule that also catches future matches."
        onClose={() => setOpen(false)}
        busy={actions.busy}
        footer={
          <Button
            label="Cancel"
            variant={ButtonVariant.SECONDARY}
            isDisabled={actions.busy}
            onClick={() => setOpen(false)}
          />
        }
      >
        {query.error ? (
          <Widget>
            <Note>
              <Alert variant={AlertVariant.ERROR}>{query.error.message}</Alert>
            </Note>
          </Widget>
        ) : !query.data ? (
          <Widget>
            {["60%", "45%"].map((width) => (
              <Note key={width}>
                <Box width={width}>
                  <Skeleton />
                </Box>
              </Note>
            ))}
          </Widget>
        ) : (
          <>
            {excluded && (
              <Block
                label="Already excluded"
                caption="Every matching rule must be paused or removed to restore this record."
              >
                {query.data.reasons.map((reason, index) => (
                  <Choice
                    key={`${reason.ruleId}:${index}`}
                    icon={ProhibitIcon}
                    title={reason.name}
                    description={`${
                      reason.field === "record"
                        ? "Exact record"
                        : (exclusionFields[reason.field as keyof typeof exclusionFields] ??
                          reason.field)
                    } matches “${reason.value}”${
                      reason.viaName ? ` through ${reason.viaName}` : ""
                    }`}
                    ariaLabel={`Manage rule: ${reason.name}`}
                    onClick={() => {
                      setOpen(false);
                      router.push(`/exclusions?exclusion=${reason.ruleId}`);
                    }}
                  />
                ))}
              </Block>
            )}
            <Block label="Exclude this record">
              {query.data.targets.length ? (
                query.data.targets.map((target) => (
                  <Choice
                    key={`${target.kind}:${target.id}`}
                    icon={targetIcons[target.kind]}
                    title={target.name}
                    description={targetHints[target.kind]}
                    ariaLabel={`Exclude ${targetWords[target.kind]}: ${target.name}`}
                    disabled={actions.busy}
                    onClick={() => {
                      const rule: ExclusionInput = {
                        ...emptyRule,
                        name: `Exclude ${target.name}`,
                        field: null,
                        operator: null,
                        value: null,
                        [`${target.kind}Id`]: target.id,
                      };
                      if (target.kind === "company") {
                        setInitial(rule);
                        setOpen(false);
                      } else
                        void actions.save(rule).then((result) => {
                          if (result) setOpen(false);
                        });
                    }}
                  />
                ))
              ) : (
                <Note>
                  <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
                    This signal has no known account to exclude. You can ignore the individual
                    signal.
                  </Text>
                </Note>
              )}
            </Block>
            {query.data.suggestions.length > 0 && (
              <Block
                label="Create a rule"
                caption="Rules keep matching records excluded as new ones arrive."
              >
                {query.data.suggestions.map((suggestion, index) => {
                  const field = exclusionFields[suggestion.field];
                  const provider = suggestion.provider
                    ? ` on ${providerNames[suggestion.provider] ?? suggestion.provider}`
                    : "";
                  return (
                    <Choice
                      key={index}
                      icon={FunnelSimpleIcon}
                      title={
                        <>
                          {`${field} equals `}
                          <Code>{suggestion.value}</Code>
                          {provider}
                        </>
                      }
                      ariaLabel={`Create rule: ${field} equals ${suggestion.value}${provider}`}
                      disabled={actions.busy}
                      onClick={() => {
                        setInitial({
                          ...emptyRule,
                          name: `Exclude ${suggestion.value}`,
                          field: suggestion.field,
                          operator: "equals",
                          value: suggestion.value,
                          provider: suggestion.provider,
                        });
                        setOpen(false);
                      }}
                    />
                  );
                })}
              </Block>
            )}
          </>
        )}
      </SurfaceDialog>
      {retainedInitial && (
        <RuleForm open={!!initial} initial={retainedInitial} onClose={() => setInitial(null)} />
      )}
    </>
  );
}
const ExclusionNotice = styled.div`
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr);
  align-items: start;
  gap: ${t.space[8]};
`;
const ExclusionReasons = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${t.space[4]};
  min-width: 0;
  a {
    color: ${t.color.text.warning};
    text-decoration: underline;
    text-underline-offset: 3px;
    overflow-wrap: anywhere;
  }
`;
export function ExclusionStatus({ kind, id }: { kind: Kind; id: string }) {
  const query = useExclusionContext(kind, id);
  if (!query.data?.reasons.length) return null;
  return (
    <Widget variant={WidgetVariant.SECONDARY}>
      <ExclusionNotice>
        <Icon component={WarningCircleIcon} variant={IconVariant.WARNING} size={20} />
        <ExclusionReasons>
          <Text size={TextSize.BODY_SM} variant={TextVariant.WARNING} weight={TextWeight.MEDIUM}>
            This {kind === "person" ? "person" : kind === "company" ? "company" : "activity"} was
            excluded by
          </Text>
          {Array.from(
            new Map(query.data.reasons.map((reason) => [reason.ruleId, reason])).values(),
          ).map((reason) => (
            <Link key={reason.ruleId} href={`/exclusions?exclusion=${reason.ruleId}`}>
              <Text size={TextSize.BODY_SM} variant={TextVariant.WARNING}>
                {reason.name}
              </Text>
            </Link>
          ))}
        </ExclusionReasons>
      </ExclusionNotice>
    </Widget>
  );
}
